import React, { useState, useEffect, useRef } from 'react';
import { Box, Line, Html } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { usePointerLockContext } from './Scene/contexts/PointerLockContext';
import { EnhancedTransformControls } from './Scene/components/EnhancedTransformControls';
import { useTransform } from '../../contexts/TransformContext';

// Constants
const DEFAULT_SIZE = 2;
const DEFAULT_FRAME_DEPTH = 0.1;
const DEFAULT_PLUS_SIZE = 0.4;
const DEFAULT_BACKGROUND_SCALE = 1.02;
const DEFAULT_FRAME_SCALE = 1.1;

const ensureNumber = (value, defaultValue = 0) => {
    return Number.isFinite(value) ? value : defaultValue;
};

const calculateSafeDimensions = (asset, dimensions, size, backgroundScale) => {
    if (asset?.url) {
        return {
            width: ensureNumber(dimensions.width * backgroundScale, size * 0.98),
            height: ensureNumber(dimensions.height * backgroundScale, size * 0.98)
        };
    }
    return {
        width: size * 0.98,
        height: size * 0.98
    };
};

const getScaleValue = (scale) => {
    if (Array.isArray(scale)) {
        return scale[0] || 1;  // Use first value or default to 1
    }
    return typeof scale === 'number' ? scale : 1;
};

// Add URL conversion helper
const convertHcsToKiloscribe = (url) => {
    if (!url) return '';
    const match = url.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
    if (match) {
        return `https://kiloscribe.com/api/inscription-cdn/${match[2]}`;
    }
    return url;
};

export function DisplaySpot({ 
    position, 
    rotation, 
    scale, 
    onSelect, 
    id, 
    asset, 
    canInteract, 
    isShowMode, 
    isSelected, 
    onTransformEnd,
    onHover
}) {
    const groupRef = useRef();
    const [hovered, setHovered] = useState(false);
    const [dimensions, setDimensions] = useState({ width: 1, height: 1 });
    const textureRef = useRef(null);
    const videoRef = useRef(null);
    const [textureReady, setTextureReady] = useState(false);
    const [isVideoTexture, setIsVideoTexture] = useState(false);
    const [convertedUrl, setConvertedUrl] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const { transformMode } = useTransform();
    const { camera } = useThree();
    
    // Convert inputs to THREE objects if they aren't already
    const positionVector = position instanceof THREE.Vector3 
        ? position 
        : new THREE.Vector3().fromArray(Array.isArray(position) ? position : [0, 0, 0]);
    
    const rotationEuler = rotation instanceof THREE.Euler 
        ? rotation 
        : new THREE.Euler().fromArray(Array.isArray(rotation) ? rotation : [0, 0, 0]);
    
    const scaleVector = scale instanceof THREE.Vector3 
        ? scale 
        : new THREE.Vector3().fromArray(Array.isArray(scale) ? scale : [1, 1, 1]);

    // Use constants
    const size = DEFAULT_SIZE;
    const frameDepth = DEFAULT_FRAME_DEPTH;
    const plusSize = DEFAULT_PLUS_SIZE;
    const maxSize = size * 0.9;
    const backgroundScale = DEFAULT_BACKGROUND_SCALE;
    const frameScale = DEFAULT_FRAME_SCALE;

    // Calculate safe dimensions for geometries
    const safeDimensions = calculateSafeDimensions(asset, dimensions, size, backgroundScale);

    const colors = {
        frame: "#ffffff",        // White frame
        selected: "#2196F3",     // Blue for selected state
        plus: "#666666",         // Darker gray for plus icon
    };

    // Add a helper function to get the background color
    const getBackgroundColor = (asset) => {
        if (!asset) return colors.backgrounds.white;
        return colors.backgrounds[asset.backgroundColor || 'white'] || colors.backgrounds.white;
    };

    // Only update from props when not selected and not during modal view
    useEffect(() => {
        if (!isSelected && groupRef.current && !asset?.isViewing) {
            groupRef.current.position.copy(positionVector);
            groupRef.current.rotation.copy(rotationEuler);
            groupRef.current.scale.copy(scaleVector);
        }
    }, [position, rotation, scale, isSelected, positionVector, rotationEuler, scaleVector, asset?.isViewing]);

    // Handle spot selection
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.code === 'KeyE' && hovered && canInteract) {
                event.preventDefault();
                onSelect?.(id, true);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [hovered, id, onSelect, canInteract]);

    // Image loading effect
    useEffect(() => {
        if (asset?.url) {
            console.log('Starting asset load for:', {
                id,
                url: asset.url,
                asset,
                isVideo: asset.isVideo
            });

            // Check if this is a video asset
            if (asset.isVideo || asset.type === 'video' || asset.url.match(/\.(mp4|webm|mov|avi)$/i)) {
                setIsVideoTexture(true);
                
                const loadVideo = () => {
                    try {
                        let videoUrl = asset.url;

                        // Handle HCS URLs for videos
                        if (videoUrl.startsWith('hcs://')) {
                            const match = videoUrl.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                            if (match) {
                                videoUrl = `https://kiloscribe.com/api/inscription-cdn/${match[2]}`;
                            }
                        }

                        setConvertedUrl(videoUrl);

                        // Create video element
                        const video = document.createElement('video');
                        video.crossOrigin = 'anonymous';
                        video.loop = true;
                        video.muted = false;
                        video.playsInline = true;
                        video.preload = 'metadata';
                        video.autoplay = false;

                        video.onloadedmetadata = () => {
                            console.log('Video loaded with dimensions:', {
                                id,
                                width: video.videoWidth,
                                height: video.videoHeight,
                                src: video.src
                            });

                            const aspectRatio = video.videoWidth / video.videoHeight;
                            const baseSize = maxSize || 2;
                            
                            let width = baseSize;
                            let height = baseSize;

                            if (aspectRatio > 1) {
                                height = width / aspectRatio;
                            } else {
                                width = height * aspectRatio;
                            }

                            const scale = getScaleValue(asset.scale);
                            width = Number.isFinite(width) ? width * scale : baseSize;
                            height = Number.isFinite(height) ? height * scale : baseSize;

                            setDimensions({ width, height });

                            // Create VideoTexture
                            const videoTexture = new THREE.VideoTexture(video);
                            videoTexture.needsUpdate = true;
                            videoTexture.colorSpace = THREE.SRGBColorSpace;
                            videoTexture.flipY = true;
                            videoTexture.minFilter = THREE.LinearFilter;
                            videoTexture.magFilter = THREE.LinearFilter;
                            
                            textureRef.current = videoTexture;
                            videoRef.current = video;
                            setTextureReady(true);

                            // Video starts paused, showing first frame
                        };

                        video.onerror = (error) => {
                            console.error('Error loading video:', {
                                id,
                                videoUrl,
                                error
                            });
                        };

                        video.src = videoUrl;
                    } catch (error) {
                        console.error('Video load error:', { id, error });
                    }
                };
                
                loadVideo();
            } else {
                setIsVideoTexture(false);
                
                const loadImage = async () => {
                try {
                    let imageUrl = asset.url;

                    // Handle HCS URLs
                    if (imageUrl.startsWith('hcs://')) {
                        const match = imageUrl.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                        if (match) {
                            const topicId = match[2];
                            console.log('Fetching HCS content:', { id, topicId });
                            
                            const response = await fetch(`https://kiloscribe.com/api/inscription-cdn/${topicId}`);
                            const content = await response.text();
                            console.log('Received HCS content:', { id, contentPreview: content.substring(0, 100) });
                            
                            try {
                                // Try to parse as JSON first
                                const metadata = JSON.parse(content);
                                console.log('Parsed metadata:', { id, metadata });
                                
                                if (metadata.image) {
                                    if (metadata.image.startsWith('hcs://')) {
                                        // Handle nested HCS URL
                                        const imageMatch = metadata.image.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                        if (imageMatch) {
                                            const imageTopicId = imageMatch[2];
                                            console.log('Fetching nested image:', { id, imageTopicId });
                                            const imageResponse = await fetch(`https://kiloscribe.com/api/inscription-cdn/${imageTopicId}`);
                                            const imageContent = await imageResponse.text();
                                            imageUrl = `data:image/svg+xml;base64,${btoa(imageContent)}`;
                                        }
                                    } else {
                                        imageUrl = metadata.image;
                                    }
                                } else {
                                    // If no image field, treat content as direct SVG
                                    imageUrl = `data:image/svg+xml;base64,${btoa(content)}`;
                                }
                            } catch (error) {
                                console.log('Not JSON, treating as direct content:', { id, error });
                                // Not JSON, treat as direct SVG content
                                imageUrl = `data:image/svg+xml;base64,${btoa(content)}`;
                            }
                        }
                    }

                    console.log('Final image URL:', { id, imageUrl });
                    setConvertedUrl(imageUrl);

                    const img = new Image();
                    img.crossOrigin = 'anonymous';

                    img.onload = () => {
                        console.log('Image loaded with dimensions:', {
                            id,
                            width: img.width,
                            height: img.height,
                            src: img.src
                        });

                        const aspectRatio = img.width / img.height;
                        const baseSize = maxSize || 2;
                        
                        let width = baseSize;
                        let height = baseSize;

                        if (aspectRatio > 1) {
                            height = width / aspectRatio;
                        } else {
                            width = height * aspectRatio;
                        }

                        const scale = getScaleValue(asset.scale);
                        width = Number.isFinite(width) ? width * scale : baseSize;
                        height = Number.isFinite(height) ? height * scale : baseSize;

                        console.log('Calculated dimensions:', {
                            id,
                            width,
                            height,
                            scale,
                            aspectRatio
                        });

                        setDimensions({ width, height });

                        // Load texture
                        const loader = new THREE.TextureLoader();
                        loader.setCrossOrigin('anonymous');
                        loader.load(
                            imageUrl,
                            (texture) => {
                                console.log('Texture loaded:', {
                                    id,
                                    dimensions: { width, height },
                                    textureSize: {
                                        width: texture.image.width,
                                        height: texture.image.height
                                    }
                                });
                                
                                texture.needsUpdate = true;
                                texture.colorSpace = THREE.SRGBColorSpace;
                                texture.flipY = true;
                                texture.minFilter = THREE.LinearFilter;
                                texture.magFilter = THREE.LinearFilter;
                                textureRef.current = texture;
                                setTextureReady(true);
                            },
                            undefined,
                            (error) => console.error('Texture load error:', { id, error })
                        );
                    };

                    img.onerror = (error) => {
                        console.error('Error loading image:', {
                            id,
                            imageUrl,
                            error: error.message
                        });
                    };

                    console.log('Setting image src:', { id, imageUrl });
                    img.src = imageUrl;
                } catch (error) {
                    console.error('Error in loadImage:', {
                        id,
                        error: error.message,
                        stack: error.stack
                    });
                }
            };

            loadImage();
            }
        }
        
        return () => {
                // Cleanup texture
                if (textureRef.current) {
                    console.log('Cleaning up texture:', { id });
                    textureRef.current.dispose();
                    textureRef.current = null;
                    setTextureReady(false);
                }
                
                // Cleanup video
                if (videoRef.current) {
                    console.log('Cleaning up video:', { id });
                    videoRef.current.pause();
                    videoRef.current.src = '';
                    videoRef.current.load();
                    videoRef.current = null;
                }
                
                setIsVideoTexture(false);
            };
    }, [asset?.url, asset?.scale, maxSize, id]);

    // Video playback control based on proximity and look direction
    useFrame(() => {
        if (isVideoTexture && videoRef.current && groupRef.current) {
            const videoSpotPosition = groupRef.current.position;
            const cameraPosition = camera.position;
            const distance = cameraPosition.distanceTo(videoSpotPosition);
            
            // Check if we're within range (adjust this value as needed)
            const maxDistance = 8;
            const isInRange = distance < maxDistance;
            
            // Check if we're looking at the video spot
            const cameraDirection = new THREE.Vector3();
            camera.getWorldDirection(cameraDirection);
            
            const spotDirection = new THREE.Vector3();
            spotDirection.subVectors(videoSpotPosition, cameraPosition).normalize();
            
            const dotProduct = cameraDirection.dot(spotDirection);
            const isLookingAt = dotProduct > 0.7; // Adjust this threshold as needed
            
            const shouldPlay = isInRange && isLookingAt;
            
            if (shouldPlay && !isPlaying) {
                videoRef.current.play().catch(console.error);
                setIsPlaying(true);
            } else if (!shouldPlay && isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    });

    // Early returns
    if (asset?.type === 'model' || asset?.modelUrl || (asset?.type === 'nft' && asset?.isModel)) {
        return null;
    }

    if (isShowMode && !asset?.url && !isSelected) {
        return null;
    }

    return (
        <>
            <group
                ref={groupRef}
                position={positionVector}
                rotation={rotationEuler}
                scale={scaleVector}
                userData={{ isDisplaySpot: true, isAsset: true, isNonCollidable: true }}
                onClick={(e) => {
                    e.stopPropagation();
                    console.log('DisplaySpot clicked:', {
                        id,
                        canInteract,
                        isShowMode,
                        hasUrl: !!asset?.url,
                        condition: canInteract && !isShowMode
                    });
                    // Only allow click interaction in edit mode
                    if (canInteract && !isShowMode) {
                        console.log('Calling onSelect with id:', id);
                        onSelect?.(id, true);
                    }
                }}
                onPointerOver={(e) => {
                    e.stopPropagation();
                    if (asset?.url || !isShowMode) {
                        setHovered(true);
                        // In view mode with asset, notify parent of hover for E interaction
                        if (isShowMode && asset?.url && onHover) {
                            onHover(id);
                        }
                    }
                }}
                onPointerOut={(e) => {
                    e.stopPropagation();
                    if (asset?.url || !isShowMode) {
                        setHovered(false);
                        // Clear hover state
                        if (isShowMode && asset?.url && onHover) {
                            onHover(null);
                        }
                    }
                }}
            >
                {/* Frame - only render if not transparent */}
                {asset?.backgroundColor !== 'transparent' && (
                    <Box args={[
                        ensureNumber(asset?.url ? dimensions.width * frameScale : size),
                        ensureNumber(asset?.url ? dimensions.height * frameScale : size),
                        ensureNumber(frameDepth)
                    ]}>
                        <meshStandardMaterial
                            color={asset?.backgroundColor || '#ffffff'}
                            metalness={0.1}
                            roughness={0.8}
                        />
                    </Box>
                )}

                {/* Background - only render if not transparent */}
                {asset?.backgroundColor !== 'transparent' && (
                    <mesh position={[0, 0, ensureNumber(frameDepth) / 2 - 0.001]}>
                        <planeGeometry args={[
                            ensureNumber(safeDimensions.width),
                            ensureNumber(safeDimensions.height)
                        ]} />
                        <meshStandardMaterial
                            color={asset?.backgroundColor || '#ffffff'}
                            metalness={0}
                            roughness={1}
                            side={THREE.DoubleSide}
                        />
                    </mesh>
                )}

                {/* Selection effect overlay - only shown when selected */}
                {isSelected && (
                    <mesh position={[0, 0, ensureNumber(frameDepth) / 2 - 0.0005]}>
                        <planeGeometry args={[
                            ensureNumber(safeDimensions.width),
                            ensureNumber(safeDimensions.height)
                        ]} />
                        <meshStandardMaterial
                            color={colors.selected}
                            metalness={0.1}
                            roughness={0.8}
                            transparent={true}
                            opacity={0.2}
                            side={THREE.DoubleSide}
                            blending={THREE.AdditiveBlending}
                        />
                    </mesh>
                )}

                {/* Display surface */}
                {asset?.url && textureRef.current && textureReady && (
                    <mesh position={[0, 0, ensureNumber(frameDepth) / 2 + 0.001]}>
                        <planeGeometry args={[
                            ensureNumber(dimensions.width),
                            ensureNumber(dimensions.height)
                        ]} />
                        <meshStandardMaterial
                            map={textureRef.current}
                            metalness={0}
                            roughness={1}
                            toneMapped={false}
                            transparent={true}
                            side={THREE.DoubleSide}
                            colorSpace={THREE.SRGBColorSpace}
                            depthWrite={false}
                            depthTest={true}
                            alphaTest={0.5}
                            premultipliedAlpha={true}
                            onUpdate={self => { self.needsUpdate = true }}
                        />
                    </mesh>
                )}

                {/* Plus icon for empty spots */}
                {!asset?.url && !isShowMode && (
                    <group position={[0, 0, ensureNumber(frameDepth) / 2 + 0.001]}>
                        <Line
                            points={[[-plusSize/2, 0, 0], [plusSize/2, 0, 0]]}
                            color={colors.plus}
                            lineWidth={2}
                        />
                        <Line
                            points={[[0, -plusSize/2, 0], [0, plusSize/2, 0]]}
                            color={colors.plus}
                            lineWidth={2}
                        />
                    </group>
                )}
            </group>

            {/* Transform Controls */}
            {isSelected && !isShowMode && (
                <EnhancedTransformControls
                    object={groupRef.current}
                    id={id}
                    onTransformEnd={onTransformEnd}
                    enabled={!isShowMode}
                    showHelpers={true}
                    uniformScale={true}
                    minScale={0.2}
                />
            )}

            {/* Transform Mode Indicator */}
            {isSelected && !isShowMode && (
                <Html
                    position={[0, 1.5, 0.1]}
                    center
                    style={{
                        background: 'rgba(0, 0, 0, 0.9)',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        whiteSpace: 'nowrap',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        opacity: 1,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
                    }}
                >
                    Press T to Move • R to Rotate • S to Scale
                </Html>
            )}
        </>
    );
} 